import {AppState, Auth0Provider} from '@auth0/auth0-react';
import env from 'constants/env';
import {routeNames} from 'constants/route-names';
import {FunctionComponent, PropsWithChildren} from 'react';
import {useNavigate} from 'react-router';

/**
 * Third-party OAuth providers redirect back to /oauth-callback/* with `code`
 * and `state` params, which the Auth0 SDK would otherwise mistake for its own
 * login callback and reject with "Invalid state". Those pages exchange the
 * code themselves, so Auth0 skips them.
 */
const isThirdPartyOAuthCallback = () =>
  window.location.pathname.startsWith(`/${routeNames.oAuthCallbackRoute}/`);

const Auth0ProviderWithRedirectCallback: FunctionComponent<
  PropsWithChildren<{}>
> = ({children}) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={env.VITE_AUTH0_DOMAIN!}
      clientId={env.VITE_AUTH0_CLIENT_ID!}
      onRedirectCallback={onRedirectCallback}
      skipRedirectCallback={isThirdPartyOAuthCallback()}
      cacheLocation="localstorage"
      useRefreshTokens
      // Defaulted to true in SDK v1 and to false in v2. Kept on so that a
      // missing or rejected refresh token still falls back to the silent
      // iframe renewal instead of surfacing an error screen.
      useRefreshTokensFallback
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: env.VITE_AUTH0_AUDIENCE!,
        // Custom params forwarded to Google for the calendar consent screen:
        // an offline refresh token plus the calendar scopes.
        access_type: 'offline',
        connection_scope: env.VITE_GOOGLE_CALENDAR_EVENTS_READONLY_SCOPE,
      }}
    >
      {children}
    </Auth0Provider>
  );
};
export default Auth0ProviderWithRedirectCallback;
